"use client";

import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import type { HrtOption } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/ProductContext";
import {
  getTranslatedProductDescription,
  getTranslatedProductKioskDescription,
  getTranslatedProductKioskTitle,
  getTranslatedProductName,
} from "@/app/directories/shop/helper";
import { ProductType } from "@/lib/graphql/generated-gql";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { EVENT_CONTEXTS } from "@frontend/lib/analytics";
import { trackEvent } from "@frontend/lib/analytics";
import { EVENT_CATEGORIES } from "@frontend/lib/analytics";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@frontend/shared/components/ui/drawer";
import { sendGTMEvent } from "@next/third-parties/google";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useLocale } from "next-intl";
import { useState } from "react";
import type React from "react";
import { PriceToggleKiosk } from "../priceToggle/priceToggleKiosk";
import { usePriceMap } from "../priceToggle/usePriceMap";

export const ProductCardCarouselSlideKiosk = ({
  product,
  clientSlug,
  orgId,
  onBuyNowClick,
}: {
  product: GetProductsByCategoryIdQuery["findManyProducts"][number];
  clientSlug: string;
  orgId: number;
  onBuyNowClick?: () => void;
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const t = useTranslations("Shop");
  const { pricingMap, isPendingPricesMode, send, getShouldShowPricingLoading } =
    useKioskEvent();

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor ?? "#000000";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    isToggleDisabled,
    lowestComparisonPrice,
    isSelfPayOnly,
  } = usePriceMap(product.productToHrt as unknown as HrtOption[], {
    pricingMap,
    onPriceModeChange: (newPricingMap) =>
      send({
        type: "SET_PRICING_MAP",
        data: { newPricingMap },
      }),
  });

  const handlePriceModeChangeKiosk = (checked: boolean) => {
    handlePriceModeChange(checked);
  };

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (product.product_type === ProductType.PdpAndBuyNow ||
        product.product_type === ProductType.BuyNow) &&
      onBuyNowClick
    ) {
      onBuyNowClick();

      sendGTMEvent({
        event: "featured_product_clicked",
        value: product.name,
      });
      trackEvent({
        category: EVENT_CATEGORIES.TRANSACTION,
        context: EVENT_CONTEXTS.STOREFRONT,
        action: "featured_product_clicked",
        metadata: {
          product_name: product.name,
        },
      });
    }
  };

  const shouldShowLoading = getShouldShowPricingLoading(
    product.productToHrt.map((option) => option.hrt.hrtId),
    lowestPrice,
  );

  return (
    <div
      key={`product-card-kiosk-${product.id}`}
      className="keen-slider__slide bg-white/10 border border-white rounded-3xl shadow-xl"
      data-component="ProductCardCarouselSlideKiosk"
      data-carousel-type="product"
      data-product-id={product.id}
      data-product-name={product.name}
      style={{
        width: "357px",
        minWidth: "357px",
        maxWidth: "357px",
        flexShrink: 0,
      }}
    >
      <div className="rounded-3xl overflow-hidden flex flex-col shadow-lg w-full bg-transparent h-full justify-between">
        {product.displayMetadata?.imageUrl && (
          <img
            src={product.displayMetadata.imageUrl}
            alt={product.name}
            style={{ height: "223px" }}
            className="object-cover w-full"
          />
        )}

        <div className="p-6 flex flex-col gap-4">
          <h3 className="kiosk-h3 !font-normal !text-2xl !leading-8">
            {getTranslatedProductName(product, languageCode)}
          </h3>
          {getTranslatedProductDescription(product, languageCode) && (
            <div className="flex flex-col gap-1">
              <p className="kiosk-body-sm line-clamp-2 !text-black tracking-tight">
                {getTranslatedProductDescription(product, languageCode)}
              </p>
              <button
                type="button"
                onClick={() => setIsDrawerOpen(true)}
                className="text-sm text-kioskPrimary underline self-start"
              >
                {t("ReadMore")}
              </button>
            </div>
          )}
        </div>

        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerContent>
            <DrawerTitle className="sr-only">
              {getTranslatedProductName(product, languageCode)} Description
            </DrawerTitle>
            <div className="py-6 md:py-12 px-6 md:px-10 min-h-[50vh] max-h-[90vh] overflow-y-auto flex flex-col justify-between">
              <div className="flex flex-col gap-4 w-full">
                <div className="flex justify-end w-full">
                  <X
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => setIsDrawerOpen(false)}
                  />
                </div>
                <h3 className=" mb-4">
                  {getTranslatedProductKioskTitle(product, languageCode)}
                </h3>
                <div className="flex flex-col gap-4">
                  {getTranslatedProductKioskDescription(
                    product,
                    languageCode,
                  ).map((description) => (
                    <p
                      key={description}
                      className="kiosk-body-lg !text-base !font-light"
                    >
                      {description}
                    </p>
                  ))}
                </div>
              </div>
              <div className="flex justify-end">
                <motion.button
                  type="button"
                  onClick={() => setIsDrawerOpen(false)}
                  className="rounded-full px-6 py-3 w-fit flex justify-center bg-transparent border hover:!bg-black hover:!text-white hover:!border-black text-sm text-center"
                  style={{
                    color: primaryColor || "#000000",
                    borderColor: primaryColor || "#000000",
                  }}
                  whileHover={{ color: "#ffffff", backgroundColor: "#000000" }}
                >
                  {t("GotIt")}
                </motion.button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>

        {/* Price Display */}
        <div className="px-6 flex items-center justify-between w-full h-10">
          <PriceToggleKiosk
            checked={isInsuredMode}
            handlePriceModeChangeKiosk={handlePriceModeChangeKiosk}
            price={lowestPrice}
            hasMultipleHrts={product.productToHrt.length > 1}
            isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
            isProductSelfPayOnly={isSelfPayOnly}
            preDiscountprice={product.displayMetadata?.preDiscount}
            pricesLoaded={pricesLoaded}
            priceToggleColor={primaryColor}
            hrtIds={product.productToHrt.map((option) => option.hrt.hrtId)}
            size="small"
          />
        </div>

        <div className="p-6 flex flex-col gap-3">
          {/* Action Button - Kiosk only has Book now */}
          <button
            type="button"
            disabled={shouldShowLoading}
            className="rounded-full text-white h-[50px] px-9 py-3 w-full text-center text-base disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: primaryColor }}
            onClick={handleBuyNowClick}
          >
            {t("BookNow")}
          </button>
        </div>
      </div>
    </div>
  );
};
